/**
 * @file Hand.tsx
 * @description
 * Represents a player's hand of cards.
 * Renders a collection of Card components.
 * Renders as a single face-down card with a count and displays the teammate's name and card count for other players.
 *
 * @dependencies
 * - React
 * - Card component
 * - ../hooks/useGameState: For GameState and Pile interface definitions
 *
 * @notes
 * - Layout is basic for now; will be improved for responsiveness and card overlap/fanning.
 * - Passes the onCardClick prop down to each Card (only if isCurrentPlayer).
 * - Integrates with click-to-play logic via `selectedCard`.
 * - Updated to show player name and card count for face-down hands.
 */

import React from 'react';
import Card from './Card';
import { Pile } from '../hooks/useGameState'; // Import Pile interface from useGameState

interface HandProps {
  /** Array of card values in the hand. */
  cards: number[];
  /** Optional click handler for individual cards. */
  onCardClick?: (cardValue: number) => void;
  /** Boolean to indicate if the hand belongs to the current player whose turn it is (this client). */
  isCurrentPlayer: boolean;
  /** The name of the player whose hand this is. Required for face-down hands. */
  playerName: string;
  /** Function to validate if a card is playable on a specific pile (needed to potentially dim non-playable cards). */
  validatePlay?: (cardValue: number, pileId: string) => boolean;
  /** The current state of all piles (needed to check playability). */
  piles: Record<string, Pile>;
  /** The card value currently selected by the player (if any). */
  selectedCard: number | null;
}

const Hand: React.FC<HandProps> = ({
  cards,
  onCardClick,
  isCurrentPlayer,
  playerName,
  validatePlay,
  piles,
  selectedCard,
}) => {

  // Basic styling for the hand container - will be improved later
  const handStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: isCurrentPlayer ? 'center' : 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '5px',
    marginTop: '5px',
    flexDirection: isCurrentPlayer ? 'row' : 'column',
  };

  const otherPlayerHandStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '5px',
    marginTop: '5px',
  };

  const playerNameStyle: React.CSSProperties = {
    fontSize: '0.9em',
    marginBottom: '5px',
    textAlign: 'center',
    maxWidth: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const cardCountStyle: React.CSSProperties = {
    fontSize: '0.8em',
    color: '#555',
    marginBottom: '5px',
  };

  if (!isCurrentPlayer) {
    // If not the current player, render player name with card count and a single face-down card
    return (
      <div
        style={otherPlayerHandStyle}
        aria-label={`Hand of ${playerName} with ${cards.length} cards`}
      >
        <Card
          isFaceDown={true}
          count={cards.length}
          isPlayable={false}
        />
      </div>
    );
  }

  // If it is the current player, render the actual cards
  return (
    <div style={handStyle} aria-label="Your hand">
      {cards
        .sort((a, b) => a - b)
        .map((cardValue) => {
          const isSelected = selectedCard === cardValue;
          const isCardPlayable = true; // All cards in your hand are potentially playable

          return (
            <Card
              key={cardValue}
              value={cardValue}
              onClick={onCardClick}
              isPlayable={isCardPlayable}
              isSelected={isSelected}
            />
          );
        })}
    </div>
  );
};

export default Hand;